import { UNIQUE_LINK_KEY } from '../constants'
import { getToken, reconfigureApi } from '../utilities/configureApi'
import SimpleStore from '../utilities/SimpleStore'
import { detectWebviewOs, isWebview } from '../utilities/userAgent'
import {
  disableHeader as androidDisableHeader,
  enableHeader as androidEnableHeader,
  loadUrl as androidLoadUrl,
} from './android'
import { iOSWebViewHandlerRegistered, postMessage } from './ios'
import { IOSNames, OsTypes, WebViewInfo } from './types'

/*
 * Enables the nav bar header
 */
export const enableHeader = () => {
  switch (detectWebviewOs()) {
    case OsTypes.iOS:
      postMessage({ name: IOSNames.IOS_ENABLE_NAVBAR_HEADER })
      break
    case OsTypes.Android:
      androidEnableHeader()
      break
  }
}

/*
 * Disables the nav bar header
 * Used in places like when submitting and you don't
 * want the user to refresh the page
 */
export const disableHeader = () => {
  switch (detectWebviewOs()) {
    case OsTypes.iOS:
      postMessage({ name: IOSNames.IOS_DISABLE_NAVBAR_HEADER })
      break
    case OsTypes.Android:
      androidDisableHeader()
      break
  }
}

declare const webview: WebViewInfo
declare const window

function cleanupIosCallbacks() {
  delete window.requestDasherCredentials
  delete window.setDasherCredentials
}

/**
 * Gets the raw WebViewInfo provided by the native iOS/Android app.
 * @returns The raw info from the WebView.
 */
function getRawWebViewInfo(): Promise<WebViewInfo> {
  return new Promise<WebViewInfo>(resolve => {
    switch (detectWebviewOs()) {
      case OsTypes.iOS:
        if (iOSWebViewHandlerRegistered()) {
          // The native app might call one of two callbacks. We'll listen for both just to cover all the bases.
          window.requestDasherCredentials = (webViewInfo: WebViewInfo) => {
            // On iOS only we backfill a couple properties on the webview object.
            window.webview.token = webViewInfo.token
            window.webview.deviceId = webViewInfo.deviceId

            resolve(webViewInfo)
            cleanupIosCallbacks()
          }

          window.setDasherCredentials = (webViewInfo: WebViewInfo) => {
            window.webview.token = webViewInfo.token

            resolve(webViewInfo)
            cleanupIosCallbacks()
          }

          postMessage({ name: IOSNames.IOS_REQUEST_CREDENTIALS })
        } else {
          resolve({})
        }
        break
      case OsTypes.Android:
        resolve({ ...webview })
        break
      default:
        resolve({})
        break
    }
  })
}

/**
 * Fetches and initializes information passed to the WebView
 * from the native iOS/Android app.
 * @returns The info from the WebView.
 */
export async function initializeWebViewInfo(): Promise<WebViewInfo> {
  const webViewInfo = await getRawWebViewInfo()
  const { token, uniqueLink } = webViewInfo

  if (token) {
    reconfigureApi(token)
  } else {
    webViewInfo.token = getToken()

    if (!webViewInfo.token) {
      throw new Error('Webview token undefined')
    }
  }

  if (uniqueLink) {
    // If we got a unique link from WebView credentials, persist it to
    // local storage.
    SimpleStore.set(UNIQUE_LINK_KEY, uniqueLink)
  } else {
    // If the native app didn't supply us with a unique link, fall back
    // to reading it from local storage.
    webViewInfo.uniqueLink = SimpleStore.get(UNIQUE_LINK_KEY)
  }

  return webViewInfo
}

/**
 * Loads URLs from inside a webview to another browser window on the device.
 * Android loads the URL after switching to the browser app. iOS loads the URL
 * within the Dasher app without any switching.
 *
 * Why: Webview pages are intended to feel like a seamless part of the
 * native app. Going from webview to webview should feel different than
 * going from webview to external website. External websites should load
 * in their own context to differentiate them from the webviews.
 *
 * Example usages:
 * - From Dasher onboarding webview, go to a USPS tracking page.
 * - From Delivery Issues "Learn more" link, go to our FAQ & help articles.
 *
 * iOS:
 * Load Url by postMessage in an within-dasher-app browser
 *
 * Android:
 * Load Url by androidLoadUrl(theUrl) in a app-switched browser
 */
export const loadUrl = url => {
  switch (detectWebviewOs()) {
    case OsTypes.iOS:
      postMessage({ name: IOSNames.IOS_LOAD_URL, message: url })
      break
    case OsTypes.Android:
      androidLoadUrl(url)
      break
  }
}

export function visitDeeplink(
  deeplink: string,
  callback?: (url: string) => void
) {
  if (!isWebview()) return

  if (callback) {
    callback(deeplink)
  }

  window.location.assign(deeplink)
}

/**
 * Loads an external link in new tab if it's in a browser
 * Opens in app if it's a webview
 */
export const loadExternalUrl = url => {
  return isWebview() ? loadUrl(url) : window.open(url, '_blank')
}
